import { supabase } from '@/integrations/supabase/client';

export interface AlertRule {
  id: string;
  name: string;
  description?: string;
  rule_type: 'error_rate' | 'performance' | 'user_activity' | 'ab_test' | 'system';
  conditions: any;
  threshold_value: number;
  threshold_operator: 'gt' | 'lt' | 'eq' | 'gte' | 'lte';
  time_window_minutes: number;
  is_active: boolean;
  severity: 'low' | 'medium' | 'high' | 'critical';
  notification_channels: string[];
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface AlertInstance {
  id: string;
  alert_rule_id: string;
  status: 'active' | 'resolved' | 'acknowledged';
  triggered_at: string;
  resolved_at?: string;
  acknowledged_at?: string;
  acknowledged_by?: string;
  trigger_value: number;
  context_data: any;
  message: string;
}

export interface NotificationChannel {
  id: string;
  name: string;
  type: 'email' | 'webhook' | 'slack' | 'browser';
  config: any;
  is_active: boolean;
  created_by?: string;
  created_at: string;
}

export interface AlertNotification {
  id: string;
  alert_instance_id: string;
  channel_id: string;
  status: 'pending' | 'sent' | 'failed';
  sent_at?: string;
  error_message?: string;
  retry_count: number;
  created_at: string;
}

export class AlertingService {
  
  /**
   * Create a new alert rule
   */
  static async createAlertRule(rule: {
    name: string;
    description?: string;
    rule_type: string;
    conditions: any;
    threshold_value: number;
    threshold_operator: string;
    time_window_minutes: number;
    severity: string;
    notification_channels: string[];
  }): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('alert_rules')
        .insert({
          name: rule.name,
          description: rule.description,
          rule_type: rule.rule_type,
          conditions: rule.conditions,
          threshold_value: rule.threshold_value,
          threshold_operator: rule.threshold_operator,
          time_window_minutes: rule.time_window_minutes,
          severity: rule.severity,
          notification_channels: rule.notification_channels,
          is_active: true
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error creating alert rule:', error);
        return null;
      }

      return data?.id || null;
    } catch (error) {
      console.error('Error in createAlertRule:', error);
      return null;
    }
  }

  /**
   * Get all alert rules
   */
  static async getAlertRules(): Promise<AlertRule[]> {
    try {
      const { data, error } = await supabase
        .from('alert_rules')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching alert rules:', error);
        return [];
      }

      return (data || []) as AlertRule[];
    } catch (error) {
      console.error('Error in getAlertRules:', error);
      return [];
    }
  }

  /**
   * Update alert rule status
   */
  static async updateAlertRuleStatus(ruleId: string, isActive: boolean): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('alert_rules')
        .update({ 
          is_active: isActive,
          updated_at: new Date().toISOString()
        })
        .eq('id', ruleId);

      if (error) {
        console.error('Error updating alert rule status:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateAlertRuleStatus:', error);
      return false;
    }
  }

  /**
   * Get active alert instances
   */
  static async getActiveAlerts(): Promise<AlertInstance[]> {
    try {
      const { data, error } = await supabase
        .from('alert_instances')
        .select(`
          *,
          alert_rules!inner(name, severity)
        `)
        .eq('status', 'active')
        .order('triggered_at', { ascending: false });

      if (error) {
        console.error('Error fetching active alerts:', error);
        return [];
      }

      return (data || []) as AlertInstance[];
    } catch (error) {
      console.error('Error in getActiveAlerts:', error);
      return [];
    }
  }

  /**
   * Get recent alert instances (last 24 hours)
   */
  static async getRecentAlerts(hours: number = 24): Promise<AlertInstance[]> {
    try {
      const since = new Date();
      since.setHours(since.getHours() - hours);

      const { data, error } = await supabase
        .from('alert_instances')
        .select(`
          *,
          alert_rules!inner(name, severity)
        `)
        .gte('triggered_at', since.toISOString())
        .order('triggered_at', { ascending: false })
        .limit(100);

      if (error) {
        console.error('Error fetching recent alerts:', error);
        return [];
      }

      return (data || []) as AlertInstance[];
    } catch (error) {
      console.error('Error in getRecentAlerts:', error);
      return [];
    }
  }

  /**
   * Acknowledge an alert
   */
  static async acknowledgeAlert(alertId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('alert_instances')
        .update({
          status: 'acknowledged',
          acknowledged_at: new Date().toISOString()
        })
        .eq('id', alertId);

      if (error) {
        console.error('Error acknowledging alert:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in acknowledgeAlert:', error);
      return false;
    }
  }

  /**
   * Resolve an alert
   */
  static async resolveAlert(alertId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('alert_instances')
        .update({
          status: 'resolved',
          resolved_at: new Date().toISOString()
        })
        .eq('id', alertId);

      if (error) {
        console.error('Error resolving alert:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in resolveAlert:', error);
      return false;
    }
  }

  /**
   * Create notification channel
   */
  static async createNotificationChannel(channel: {
    name: string;
    type: string;
    config: any;
  }): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('notification_channels')
        .insert({
          name: channel.name,
          type: channel.type,
          config: channel.config,
          is_active: true
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error creating notification channel:', error);
        return null;
      }

      return data?.id || null;
    } catch (error) {
      console.error('Error in createNotificationChannel:', error);
      return null;
    }
  }

  /**
   * Get notification channels
   */
  static async getNotificationChannels(): Promise<NotificationChannel[]> {
    try {
      const { data, error } = await supabase
        .from('notification_channels')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching notification channels:', error);
        return [];
      }

      return (data || []) as NotificationChannel[];
    } catch (error) {
      console.error('Error in getNotificationChannels:', error);
      return [];
    }
  }

  /**
   * Trigger alert processing (call the edge function)
   */
  static async processAlerts(): Promise<boolean> {
    try {
      const { data, error } = await supabase.functions.invoke('process-alerts');

      if (error) {
        console.error('Error calling alert processor:', error);
        return false;
      }

      console.log('Alert processing result:', data);
      return true;
    } catch (error) {
      console.error('Error in processAlerts:', error);
      return false;
    }
  }

  /**
   * Get alert statistics
   */
  static async getAlertStats(days: number = 7): Promise<{
    total_alerts: number;
    active_alerts: number;
    resolved_alerts: number;
    acknowledged_alerts: number;
    critical_alerts: number;
    alert_trend: Array<{ date: string; count: number }>;
  }> {
    try {
      const since = new Date();
      since.setDate(since.getDate() - days);

      // Get total counts
      const [totalRes, activeRes, resolvedRes, acknowledgedRes] = await Promise.all([
        supabase
          .from('alert_instances')
          .select('id', { count: 'exact' })
          .gte('triggered_at', since.toISOString()),
        
        supabase
          .from('alert_instances')
          .select('id', { count: 'exact' })
          .eq('status', 'active'),
        
        supabase
          .from('alert_instances')
          .select('id', { count: 'exact' })
          .eq('status', 'resolved')
          .gte('triggered_at', since.toISOString()),
        
        supabase
          .from('alert_instances')
          .select('id', { count: 'exact' })
          .eq('status', 'acknowledged')
          .gte('triggered_at', since.toISOString())
      ]);

      // Get critical alerts (assuming we join with alert_rules)
      const { data: criticalData } = await supabase
        .from('alert_instances')
        .select(`
          id,
          alert_rules!inner(severity)
        `)
        .eq('alert_rules.severity', 'critical')
        .eq('status', 'active');

      // Get daily trend
      const { data: trendData } = await supabase
        .from('alert_instances')
        .select('triggered_at')
        .gte('triggered_at', since.toISOString());

      // Process trend data
      const trendMap = new Map<string, number>();
      (trendData || []).forEach(alert => {
        const date = new Date(alert.triggered_at).toISOString().split('T')[0];
        trendMap.set(date, (trendMap.get(date) || 0) + 1);
      });

      const alert_trend = Array.from(trendMap.entries())
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));

      return {
        total_alerts: totalRes.count || 0,
        active_alerts: activeRes.count || 0,
        resolved_alerts: resolvedRes.count || 0,
        acknowledged_alerts: acknowledgedRes.count || 0,
        critical_alerts: criticalData?.length || 0,
        alert_trend
      };
    } catch (error) {
      console.error('Error in getAlertStats:', error);
      return {
        total_alerts: 0,
        active_alerts: 0,
        resolved_alerts: 0,
        acknowledged_alerts: 0,
        critical_alerts: 0,
        alert_trend: []
      };
    }
  }
}